import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PROJECTS as ALL_PROJECTS } from "@/lib/projects";

const MotionLink = motion(Link);

// Homepage bento grid — reuses the shared project registry (lib/projects.js)
// as the single source of truth for id/title/year/result/img/logo, adding
// only the layout-specific `category` label and grid `span` per card.
const BENTO_LAYOUT = {
    hks: {
        category: "Branding · Performance",
        span: "lg:col-span-7 lg:row-span-2 aspect-[4/5] lg:aspect-auto",
    },
    karnani: {
        category: "Real Estate · Growth",
        span: "lg:col-span-5 aspect-[4/3]",
    },
    "gv-ecostays": {
        category: "Hospitality · Brand",
        span: "lg:col-span-5 aspect-[4/3]",
    },
    "sudama-farms": {
        category: "Agriculture · D2C",
        span: "lg:col-span-7 aspect-[16/9]",
    },
    logoinfosoft: {
        category: "Technology · SaaS",
        span: "lg:col-span-5 aspect-[4/3]",
    },
    scf: {
        category: "Non-profit · Mobility",
        span: "lg:col-span-6 aspect-[4/3]",
    },
    sbca: {
        category: "Education · F&B",
        span: "lg:col-span-6 aspect-[4/3]",
    },
};

const PROJECTS = ALL_PROJECTS.map((p) => ({
    ...p,
    ...BENTO_LAYOUT[p.id],
}));

const ease = [0.22, 1, 0.36, 1];

export default function Work() {
    return (
        <section
            id="work"
            data-testid="work-section"
            className="relative py-24 lg:py-32"
        >
            <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14 lg:mb-20">
                    <div>
                        <div className="text-xs uppercase tracking-[0.22em] font-bold text-black/55">
                            (Selected work / 02)
                        </div>
                        <h2 className="mt-4 font-display font-black uppercase tracking-tight leading-[0.9] text-5xl sm:text-6xl lg:text-7xl">
                            Brands we&apos;ve grown.
                        </h2>
                    </div>
                    <p className="font-serif italic text-xl lg:text-2xl text-black/60 max-w-md">
                        Seven brands across hospitality, real estate,
                        manufacturing, D2C, education, technology and beyond.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
                    {PROJECTS.map((p, i) => (
                        <MotionLink
                            key={p.id}
                            to={`/work/${p.id}`}
                            data-testid={`work-card-${p.id}`}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{
                                duration: 0.9,
                                ease,
                                delay: (i % 4) * 0.06,
                            }}
                            className={`group relative block overflow-hidden bg-black/[0.04] ${p.span}`}
                        >
                            <img
                                src={p.img}
                                alt={p.title}
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
                                loading="lazy"
                            />

                            {/* Top meta */}
                            <div className="absolute top-4 left-4 right-4 flex items-center justify-between gap-3 z-10">
                                <div className="flex items-center gap-3">
                                    {p.logo && (
                                        <span className="inline-flex items-center justify-center bg-bone/95 backdrop-blur-sm px-2 py-1 h-8 transition-all duration-500 group-hover:bg-bone">
                                            <img
                                                src={p.logo}
                                                alt=""
                                                aria-hidden
                                                className="h-4 lg:h-5 w-auto object-contain"
                                            />
                                        </span>
                                    )}
                                    <span className="text-bone text-[10px] uppercase tracking-[0.18em] font-bold mix-blend-difference">
                                        {p.category}
                                    </span>
                                </div>
                                <span className="text-bone text-[10px] uppercase tracking-[0.18em] font-bold mix-blend-difference">
                                    {p.year}
                                </span>
                            </div>

                            {/* Scrim — deeper on hover */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent opacity-70 group-hover:opacity-100 transition-opacity duration-700" />

                            {/* Title block */}
                            <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8 translate-y-2 group-hover:translate-y-0 transition-transform duration-500 z-10">
                                <div className="flex items-end justify-between gap-4">
                                    <h3 className="font-display font-bold text-xl sm:text-2xl lg:text-3xl text-bone tracking-tight leading-tight">
                                        {p.title}
                                    </h3>
                                    <span className="text-[#A8C48A] text-xs uppercase tracking-[0.18em] font-bold shrink-0 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-500">
                                        {p.result} ↗
                                    </span>
                                </div>
                                {/* Baseline metric (always visible on cards without hover) */}
                                <div className="mt-2 text-[10px] uppercase tracking-[0.2em] font-bold text-bone/80 opacity-100 group-hover:opacity-0 transition-opacity duration-300">
                                    {p.result}
                                </div>
                            </div>
                        </MotionLink>
                    ))}
                </div>

                {/* View all */}
                <div className="mt-12 lg:mt-16 flex items-center justify-between border-t border-black/10 pt-8">
                    <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/50">
                        Seven partners · Real outcomes
                    </span>
                    <Link
                        to="/work"
                        data-testid="work-section-cta"
                        className="group inline-flex items-center gap-3 text-xs uppercase tracking-[0.2em] font-bold link-underline"
                    >
                        View all case studies
                        <span
                            aria-hidden
                            className="transition-transform duration-300 group-hover:translate-x-1"
                        >
                            →
                        </span>
                    </Link>
                </div>
            </div>
        </section>
    );
}
